/**
 * ヘッダーの絞り込み検索ドロワー。
 *
 * 開閉そのものは既存の menu-drawer が担当する。この要素が受け持つのは
 *   1. 初回オープン時に、フィルター項目を Section Rendering API から取得して差し込む
 *   2. 「フィルターを適用する」で検索結果ページへ遷移する
 *   3. 「すべてクリア」で選択状態を初期化する
 * の3つ。
 *
 * 検索結果ページの facet-filters-form は「今開いているURL」を基準に再取得する作りなので、
 * トップページなどから開くヘッダーのドロワーでは使わず、ここで独自に処理している。
 */
class HeaderFilterDrawer extends HTMLElement {
  constructor() {
    super();

    this.hasLoaded = false;
    this.isLoading = false;
  }

  connectedCallback() {
    this.body = this.querySelector('[data-filter-body]');
    this.summary = this.querySelector('summary');
    this.sectionUrl = this.dataset.sectionUrl;
    this.searchUrl = this.dataset.searchUrl || '/search';

    if (this.summary) {
      this.summary.addEventListener('click', () => this.loadFilters());
    }

    const applyButton = this.querySelector('[data-filter-apply]');
    if (applyButton) {
      applyButton.addEventListener('click', event => {
        event.preventDefault();
        this.applyFilters();
      });
    }

    const clearButton = this.querySelector('[data-filter-clear]');
    if (clearButton) {
      clearButton.addEventListener('click', event => {
        event.preventDefault();
        this.clearFilters();
      });
    }

    /** キーワード欄で Enter を押したときも絞り込みを実行する */
    const keyword = this.querySelector('[data-filter-keyword]');
    if (keyword) {
      keyword.addEventListener('keydown', event => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.applyFilters();
      });
    }
  }

  async loadFilters() {
    if (this.hasLoaded || this.isLoading || !this.body) return;

    this.isLoading = true;

    try {
      const response = await fetch(this.sectionUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = doc.querySelector('[data-filter-content]');
      if (!content) throw new Error('フィルターの中身が見つかりませんでした');

      this.body.innerHTML = content.innerHTML;
      this.hasLoaded = true;

      /** 「もっと見る」は facets.js の静的メソッドが担当しているため、差し込み後に呼び直す */
      if (typeof FacetFiltersForm !== 'undefined') {
        FacetFiltersForm.initializeShowMore();
      }

      /** 選択肢の列幅も、中身が入ってから計測し直す */
      if (typeof window.layoutFacetLists === 'function') {
        window.layoutFacetLists(this);
      }
    } catch (error) {
      console.error('[header-filter-drawer]', error);
      this.body.innerHTML = `<p class="header-filter-drawer__message">${this.body.dataset.errorText || ''}</p>`;
    } finally {
      this.isLoading = false;
    }
  }

  applyFilters() {
    const params = new URLSearchParams();

    /** キーワード未入力なら全商品を対象にする */
    const keyword = this.querySelector('[data-filter-keyword]');
    params.set('q', (keyword && keyword.value.trim()) || '*');
    params.set('type', 'product');
    params.set('options[prefix]', 'last');

    this.querySelectorAll('[data-filter-body] input[type="checkbox"]:checked').forEach(input => {
      if (input.name) params.append(input.name, input.value);
    });

    /** 価格は範囲いっぱいのままなら、絞り込んでいないので送らない */
    this.querySelectorAll('[data-filter-body] input[type="number"]').forEach(input => {
      const value = input.valueAsNumber;
      if (!input.name || Number.isNaN(value)) return;

      const isUntouchedMin = input.name.endsWith('.gte') && value <= Number(input.min);
      const isUntouchedMax = input.name.endsWith('.lte') && value >= Number(input.max);
      if (isUntouchedMin || isUntouchedMax) return;

      params.append(input.name, value);
    });

    window.location.href = `${this.searchUrl}?${params.toString()}`;
  }

  clearFilters() {
    const keyword = this.querySelector('[data-filter-keyword]');
    if (keyword) keyword.value = '';

    this.querySelectorAll('[data-filter-body] input[type="checkbox"]').forEach(input => {
      input.checked = false;
    });

    this.querySelectorAll('[data-filter-body] input[type="number"]').forEach(input => {
      input.value = input.name.endsWith('.gte') ? input.min : input.max;
    });

    this.querySelectorAll('[data-filter-body] .facets__item--checked').forEach(item => {
      item.classList.remove('facets__item--checked');
    });
  }
}

customElements.define('header-filter-drawer', HeaderFilterDrawer);
